import { jsx, jsxs } from "react/jsx-runtime";
import { Box, Stack, IconClear, Text, IconTick, Heading, Columns, Column } from "braid-design-system";
import { getProductIsSelectedLoading, getGridColumn, HelpTip } from "../../GridLayout.mjs";
import { Grid } from "../../../shared/components/Grid/Grid.mjs";
import { GridColumn } from "../../../shared/components/Grid/GridColumn/GridColumn.mjs";
import { getPanelStyle } from "../../../shared/utils/getPanelStyle/getPanelStyle.mjs";
import { panelReset, panelValuesFeatured, panelValuesFeaturedSelected, panelValues, panelValuesSelected, comparativeMatrixColumn, panel, comparativeCellWrapper } from "./ComparativesMatrix.css.mjs";
import { gridContainer } from "../../GridLayout.css.mjs";
import { panelShared } from "../../../shared/SharedLayout.css.mjs";
const splitStringByPlusSign = (inputString) => {
  if (inputString === "") {
    return [""];
  }
  const parts = inputString.split("+").map((part) => part.trim());
  const result = [];
  parts.forEach((part, index) => {
    if (index > 0) {
      result.push("+");
    }
    if (part !== "") {
      result.push(part);
    }
  });
  return result;
};
const getCellValue = (value) => {
  var _a;
  if (!value) return /* @__PURE__ */ jsx(IconClear, { data: { testid: "comparative-clear" } });
  if (value.content && value.key === "competitive-edge") {
    const hasPlusInContent = (_a = value.content) == null ? void 0 : _a.includes("+");
    if (!hasPlusInContent) {
      return /* @__PURE__ */ jsx(Text, { children: value.content });
    }
    const splitContent = splitStringByPlusSign(value.content);
    return splitContent.map((content, index) => /* @__PURE__ */ jsx(Text, { children: content }, index));
  }
  if (value.content) return /* @__PURE__ */ jsx(Text, { children: value.content });
  if (value.type === "included") {
    return /* @__PURE__ */ jsx(
      Box,
      {
        background: "positiveLight",
        borderRadius: "full",
        style: { padding: "3px" },
        children: /* @__PURE__ */ jsx(IconTick, { size: "small", data: { testid: "comparative-tick" } })
      }
    );
  }
  return /* @__PURE__ */ jsx(IconClear, { data: { testid: "comparative-clear" } });
};
const ComparativeValueCell = ({
  value
}) => /* @__PURE__ */ jsx(
  Box,
  {
    display: "flex",
    alignItems: "center",
    height: "full",
    width: "full",
    justifyContent: "center",
    children: /* @__PURE__ */ jsx(Stack, { space: "small", align: "center", children: getCellValue(value) })
  }
);
const filterAttributes = (products) => (attribute) => products.find(
  (product) => {
    var _a, _b;
    return Boolean(
      (_b = (_a = product == null ? void 0 : product.comparatives) == null ? void 0 : _a.content) == null ? void 0 : _b.find(
        (comparative) => comparative.key === attribute.key && comparative.type === "included"
      )
    );
  }
);
const ComparativesMatrix = ({
  numOfColumns,
  products,
  selectedProductIds,
  selectedParentProduct,
  variant = "default",
  sections
}) => /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(Stack, { space: "medium", children: sections.map(({ title, attributes }) => /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
  /* @__PURE__ */ jsx(ComparativesMatrixSectionTitle, { title }),
  /* @__PURE__ */ jsx(
    ComparativesMatrixSection,
    {
      ...{
        numOfColumns,
        attributes: attributes.filter(filterAttributes(products)),
        variant,
        products,
        selectedProductIds,
        selectedParentProduct,
        gridTemplateAreas: attributes.map((attribute) => attribute.key).join(" ")
      }
    }
  )
] }, title)) }) });
const ComparativesMatrixSection = ({
  numOfColumns,
  attributes,
  variant,
  products,
  selectedProductIds,
  selectedParentProduct,
  gridTemplateAreas
}) => /* @__PURE__ */ jsxs(Grid, { className: [gridContainer], children: [
  /* @__PURE__ */ jsx(
    ComparativesMatrixColumn,
    {
      numOfColumns,
      customStyles: [panelReset],
      gridTemplateAreas,
      children: attributes.map((attribute) => /* @__PURE__ */ jsx(ComparativeCellWrapper, { children: /* @__PURE__ */ jsx(ComparativeHeadingCell, { attribute, variant }) }, attribute.key))
    }
  ),
  products.map((product, index) => {
    var _a;
    const isCurrentProduct = product.purchaseState === "PURCHASED";
    const { isSelected } = getProductIsSelectedLoading({
      selectedProductIds,
      loadingProductIds: [],
      product,
      forceSelected: isCurrentProduct && !selectedParentProduct
    });
    const isFeaturedColumn = ((_a = product.banner) == null ? void 0 : _a.type) === "recommended-featured";
    const columnStylesDefault = [
      panelValues,
      isSelected && !isCurrentProduct ? panelValuesSelected : ""
    ];
    const columnStylesWhenFeatured = [
      panelValuesFeatured,
      isSelected && !isCurrentProduct ? panelValuesFeaturedSelected : ""
    ];
    return /* @__PURE__ */ jsx(
      ComparativesMatrixColumn,
      {
        index: index + 1,
        numOfColumns,
        isSelected,
        isCurrentProduct,
        customStyles: [
          panelReset,
          ...isFeaturedColumn ? columnStylesWhenFeatured : columnStylesDefault
        ],
        gridTemplateAreas,
        children: attributes.map((attribute) => {
          var _a2;
          const value = (_a2 = product.comparatives) == null ? void 0 : _a2.content.find(
            ({ key }) => key === attribute.key
          );
          return /* @__PURE__ */ jsx(ComparativeCellWrapper, { children: /* @__PURE__ */ jsx(ComparativeValueCell, { value }) }, attribute.key);
        })
      },
      product.id
    );
  })
] });
const ComparativesMatrixSectionTitle = ({ title }) => /* @__PURE__ */ jsx(Box, { style: { borderBottom: "1px solid #5A6881" }, paddingBottom: "gutter", children: /* @__PURE__ */ jsx(Heading, { level: "4", align: "left", children: title }) });
const ComparativesMatrixColumn = ({
  numOfColumns,
  index = 0,
  children,
  isSelected = false,
  isCurrentProduct = false,
  customStyles,
  gridTemplateAreas
}) => /* @__PURE__ */ jsx(
  GridColumn,
  {
    className: [
      panelShared,
      comparativeMatrixColumn,
      getPanelStyle({
        panel: { variant: "default" },
        isSelected,
        isCurrentProduct,
        productType: "AdProduct"
      }),
      isSelected && !isCurrentProduct ? panel : "",
      ...customStyles ?? []
    ],
    gridColumns: getGridColumn(index, numOfColumns),
    gridTemplateAreas,
    gridRowGap: "0",
    children
  }
);
const ComparativeCellWrapper = ({
  key,
  children
}) => /* @__PURE__ */ jsx(Box, { className: comparativeCellWrapper, style: { gridArea: key }, children });
const ComparativeHeadingCell = ({
  attribute,
  variant
}) => /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
  /* @__PURE__ */ jsxs(Columns, { space: "small", alignY: variant === "compact" ? "center" : "top", children: [
    /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(Text, { weight: "strong", children: attribute.title }) }),
    /* @__PURE__ */ jsx(Column, { width: "content", children: attribute.helpTip ? /* @__PURE__ */ jsx(
      HelpTip,
      {
        name: attribute.title,
        helpTip: attribute.helpTip,
        tone: "secondary",
        tracking: { id: attribute.key }
      }
    ) : null })
  ] }),
  variant === "default" && attribute.description ? /* @__PURE__ */ jsx(Text, { children: attribute.description }) : null
] });
export {
  ComparativesMatrix
};
